import { useState, useEffect, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/auth/hooks/useAuth';
import { GUEST_TRIAL_ENDED_CTA, GUEST_TRIAL_ENDED_TITLE } from '@/credits/guest-trial-cta';
import { api } from '@/lib/api';
import {
  CompanyAuthBrandHeader,
  SecuredByFias,
  markRegisteredHere,
  useCompanyAuthBrand,
} from '@/auth/company/CompanyAuthBrand';

type SignupStep = 'form' | 'confirm';

// Session storage key for signup data (survives page refresh but not tab close)
const SIGNUP_DATA_KEY = 'fias_signup_pending';

interface PendingSignupData {
  email: string;
  username: string;
  inviteCode: string;
}

function getPendingSignupData(): PendingSignupData | null {
  try {
    const data = sessionStorage.getItem(SIGNUP_DATA_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

function setPendingSignupData(data: PendingSignupData): void {
  sessionStorage.setItem(SIGNUP_DATA_KEY, JSON.stringify(data));
}

function clearPendingSignupData(): void {
  sessionStorage.removeItem(SIGNUP_DATA_KEY);
}

export function SignupPage() {
  // Initialize state from session storage if available (for page refresh recovery)
  const pendingData = getPendingSignupData();
  const [step, setStep] = useState<SignupStep>(pendingData ? 'confirm' : 'form');
  const [username, setUsername] = useState(pendingData?.username || '');
  const [usernameError, setUsernameError] = useState('');
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [email, setEmail] = useState(pendingData?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState(pendingData?.inviteCode || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { signUp, confirmSignUp, isGuest } = useAuth();
  const navigate = useNavigate();
  // The Company-branded registration (ADR-024 §4.12) is this page with the
  // portal's band, returning to the branded sign-in and then Continue.
  const companyBrand = useCompanyAuthBrand();
  const signInPath = companyBrand?.paths.signIn ?? '/auth/login';
  const forgotPath = companyBrand?.paths.forgot ?? '/auth/forgot-password';
  // Where signing in should lead afterwards (a Company portal's Continue step
  // passes it). Carried on to sign-in, so registering never loses it — even
  // where the sessionStorage fallback is unavailable.
  const location = useLocation();
  const from = (location.state as { from?: unknown } | null)?.from;
  const carryFrom = typeof from === 'string' ? { from } : {};
  // A guest trial may be able to stand in for the invite code: if the guest was
  // minted from an ad click, the server (the Cognito PreSignUp trigger) admits
  // the sign-up without one. That is the server's call — here the field just
  // becomes optional, and the server's message is shown if it says no.
  const inviteOptional = isGuest;
  // Why a guest was sent here (`?reason=`): its trial credits ran out
  // (`trial-ended`) or it reached something only a full account may do
  // (`create-account`). Either way the account it signs up with is the same
  // one, upgraded in place, so everything it made stays.
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');
  const guestReasonLine =
    !isGuest || companyBrand
      ? null
      : reason === 'trial-ended'
        ? `${GUEST_TRIAL_ENDED_TITLE}. ${GUEST_TRIAL_ENDED_CTA} — everything you made stays yours.`
        : reason === 'create-account'
          ? 'That needs a free Fias account. Create one to keep going — everything you made stays yours.'
          : null;

  // Debounced username availability check
  useEffect(() => {
    // Clear error when username changes
    setUsernameError('');

    // Don't check if username is too short
    if (username.length < 3) {
      return;
    }

    // Basic format validation before API call
    const usernameRegex = /^[a-zA-Z0-9][a-zA-Z0-9_-]*[a-zA-Z0-9]$|^[a-zA-Z0-9]$/;
    if (!usernameRegex.test(username)) {
      setUsernameError('Username must start and end with a letter or number');
      return;
    }

    if (/[-_]{2,}/.test(username)) {
      setUsernameError('Username cannot contain consecutive underscores or dashes');
      return;
    }

    // Debounce the API call
    const timer = setTimeout(async () => {
      setIsCheckingUsername(true);
      try {
        const response = await api.get<{ available: boolean }>('/auth/check-username', {
          params: { username },
        });
        if (!response.data.available) {
          setUsernameError('This username is already taken');
        }
      } catch {
        // Silently fail - user will get error on submit
      } finally {
        setIsCheckingUsername(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [username]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pwd)) return 'Password must contain an uppercase letter';
    if (!/[a-z]/.test(pwd)) return 'Password must contain a lowercase letter';
    if (!/[0-9]/.test(pwd)) return 'Password must contain a number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) return 'Password must contain a special character';
    return null;
  };

  const validateUsername = (name: string): string | null => {
    if (name.length < 3) return 'Username must be at least 3 characters';
    if (name.length > 30) return 'Username must be 30 characters or less';
    if (!/^[a-zA-Z0-9][a-zA-Z0-9_-]*[a-zA-Z0-9]$/.test(name) && !/^[a-zA-Z0-9]$/.test(name)) {
      return 'Username must start and end with a letter or number';
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(name)) {
      return 'Username can only contain letters, numbers, underscores, and dashes';
    }
    if (/[-_]{2,}/.test(name)) {
      return 'Username cannot contain consecutive underscores or dashes';
    }
    return null;
  };

  const handleSignup = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate username
    const usernameValidationError = validateUsername(username);
    if (usernameValidationError) {
      setError(usernameValidationError);
      return;
    }

    // Check if there's a username availability error
    if (usernameError) {
      setError(usernameError);
      return;
    }

    // Validate password match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Validate terms acceptance
    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service, Privacy Policy, and Acceptable Use Policy');
      return;
    }

    setIsLoading(true);

    try {
      const normalizedInviteCode = inviteCode.trim().toUpperCase();
      // Empty → omitted from the request (auth-api), never sent as "".
      const result = await signUp(email, password, normalizedInviteCode || undefined, username);
      if (result.requiresConfirmation) {
        // Save signup data to session storage for page refresh recovery
        setPendingSignupData({ email, username, inviteCode: normalizedInviteCode });
        setStep('confirm');
      } else {
        clearPendingSignupData();
        if (companyBrand) markRegisteredHere(companyBrand.hostId);
        navigate(signInPath, {
          state: { message: 'Account created! Please sign in.', ...carryFrom },
        });
      }
    } catch (err) {
      // The address already has an account made on a Company portal, with no
      // password (ADR-024 §10): the server has emailed a code to set one.
      if (signUpErrorCode(err) === 'PASSWORD_NOT_SET') {
        navigate(forgotPath, {
          state: { mode: 'set', email, codeSent: true, ...carryFrom },
        });
        return;
      }
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Use stored values to ensure they survive page refresh
      const normalizedInviteCode = inviteCode.trim().toUpperCase();
      await confirmSignUp(email, verificationCode, normalizedInviteCode, username);
      // Clear pending signup data after successful confirmation
      clearPendingSignupData();
      if (companyBrand) markRegisteredHere(companyBrand.hostId);
      navigate(signInPath, {
        state: { message: 'Email verified! Please sign in.', ...carryFrom },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'confirm') {
    return (
      <div className="min-h-screen flex flex-col bg-linear-to-b from-background to-muted/30">
        <div className="flex-1 flex items-center justify-center px-4 pb-20">
          <div className="w-full max-w-sm">
            {/* Logo/Brand */}
            <div className="text-center mb-8">
              <img
                src="/fias-logo.png"
                alt="Fias"
                className="h-16 w-auto mx-auto mb-4 dark:invert"
              />
              <h1 className="text-2xl font-semibold text-foreground">Verify your email</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                We sent a code to <strong>{email}</strong>
              </p>
            </div>

            {/* Card */}
            <div className="bg-card rounded-xl border shadow-sm p-6">
              <form onSubmit={handleConfirm} className="space-y-4">
                {error && (
                  <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3">
                    <p className="text-sm text-destructive">{error}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="code">Verification code</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    required
                    autoComplete="one-time-code"
                    disabled={isLoading}
                    maxLength={6}
                    className="h-11 text-center font-mono tracking-widest text-lg"
                  />
                </div>

                <Button type="submit" className="w-full h-11" disabled={isLoading}>
                  {isLoading ? 'Verifying...' : 'Verify email'}
                </Button>
              </form>
            </div>

            {/* Help links */}
            <div className="mt-6 space-y-2 text-center">
              <p className="text-sm text-muted-foreground">
                Didn't receive the code?{' '}
                <button
                  type="button"
                  className="text-primary font-medium hover:underline"
                  onClick={() => {
                    // TODO: Implement resend
                  }}
                >
                  Resend
                </button>
              </p>
              <p className="text-sm text-muted-foreground">
                Wrong email?{' '}
                <button
                  type="button"
                  className="text-primary font-medium hover:underline"
                  onClick={() => {
                    clearPendingSignupData();
                    setStep('form');
                    setEmail('');
                    setUsername('');
                    setInviteCode('');
                    setVerificationCode('');
                    setError('');
                  }}
                >
                  Start over
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-linear-to-b from-background to-muted/30">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          {/* Logo/Brand */}
          {companyBrand && <CompanyAuthBrandHeader brand={companyBrand} />}
          <div className="text-center mb-8">
            {!companyBrand && (
              <img
                src="/fias-logo.png"
                alt="Fias"
                className="h-16 w-auto mx-auto mb-4 dark:invert"
              />
            )}
            <h1 className="text-2xl font-semibold text-foreground">Create an account</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {companyBrand
                ? 'One Fias account, for this portal and all of Fias'
                : 'Join the Fias beta'}
            </p>
            {guestReasonLine && (
              <p role="status" className="mt-3 text-sm text-foreground">
                {guestReasonLine}
              </p>
            )}
          </div>

          {/* Card */}
          <div className="bg-card rounded-xl border shadow-sm p-6">
            <form onSubmit={handleSignup} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-code">
                    Invite code
                    {inviteOptional && (
                      <span className="ml-1 font-normal text-muted-foreground">(optional)</span>
                    )}
                  </Label>
                  <Input
                    id="invite-code"
                    type="text"
                    placeholder="Enter your invite code"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    // Not `required`: the server decides (the PreSignUp trigger
                    // is the gate), and an address that already has a portal
                    // account is sent to set a password with no code at all.
                    disabled={isLoading}
                    className="h-11 font-mono tracking-wider"
                  />
                  <p className="text-xs text-muted-foreground">
                    {inviteOptional
                      ? 'Not needed — your free trial lets you create an account.'
                      : "Don't have a code? Contact the Fias team."}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <div className="relative">
                    <Input
                      id="username"
                      type="text"
                      placeholder="your_username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoComplete="username"
                      disabled={isLoading}
                      className={`h-11 ${usernameError ? 'border-destructive' : ''}`}
                    />
                    {isCheckingUsername && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                      </div>
                    )}
                  </div>
                  {usernameError && <p className="text-xs text-destructive">{usernameError}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    disabled={isLoading}
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <PasswordInput
                    id="password"
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    disabled={isLoading}
                    className="h-11"
                  />
                  <p className="text-xs text-muted-foreground">
                    8+ chars with upper, lower, number & special char.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm password</Label>
                  <PasswordInput
                    id="confirm-password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    disabled={isLoading}
                    className="h-11"
                  />
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="agree-terms"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked)}
                  disabled={isLoading}
                  className="mt-0.5"
                />
                <label
                  htmlFor="agree-terms"
                  className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
                >
                  I agree to the{' '}
                  <Link to="/legal/terms" className="text-primary hover:underline" target="_blank">
                    Terms of Service
                  </Link>
                  ,{' '}
                  <Link
                    to="/legal/privacy"
                    className="text-primary hover:underline"
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>
                  , and{' '}
                  <Link
                    to="/legal/acceptable-use"
                    className="text-primary hover:underline"
                    target="_blank"
                  >
                    Acceptable Use Policy
                  </Link>
                </label>
              </div>

              <Button
                type="submit"
                className="w-full h-11 mt-2"
                disabled={isLoading || !agreedToTerms}
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>
          </div>

          {/* Sign in link */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to={signInPath} className="text-primary font-medium hover:underline">
              Sign in
            </Link>
          </p>
          {companyBrand && <SecuredByFias />}
        </div>
      </div>

      {/* Footer */}
      <div className="py-4 text-center text-xs text-muted-foreground space-x-3">
        <Link to="/legal/terms" className="hover:text-foreground transition-colors">
          Terms of Service
        </Link>
        <span>·</span>
        <Link to="/legal/privacy" className="hover:text-foreground transition-colors">
          Privacy Policy
        </Link>
        <span>·</span>
        <Link to="/legal/acceptable-use" className="hover:text-foreground transition-colors">
          Acceptable Use Policy
        </Link>
      </div>
    </div>
  );
}

/** The `code` an `authFetch` error carries, if any. */
function signUpErrorCode(err: unknown): string | undefined {
  return typeof err === 'object' && err !== null && 'code' in err
    ? (err as { code?: string }).code
    : undefined;
}
